import React from 'react';
import styles from './ActionBar.module.css';
import { Col, Row } from 'react-bootstrap';
import { FloMenuButton, FloGroupedDropdown, FloFilterGroup, FloDropdown, FloAlertFilterCheckbox } from 'components';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { ConfirmationPopupFunction } from 'effects';
import { AlarmLabel, AlertActionFilter, Location, LocationType, Message, MessageType } from 'model';
import { AlertTab, ListView } from "../../model/state";
import AlertExportButton from 'containers/AlertExportButton';

type ActionBarProps = {
  isAnySelected:          boolean,
  selectedNumber:         number,
  openConfirmationPopup:  ConfirmationPopupFunction,
  alertsToResolveNumber:  number,
  showMessage:            (message: Message) => void,
  clearBulkAlerts:        () => void,
  isBulkResolveEnabled:   boolean,
  fetchUserLocations:     (userId: string, types?: LocationType[], page?: number, search?: string) => void,
  userLocations:          ListView<Location>,
  userId:                 string | null | undefined,
  currentTab:             AlertTab,
  search:                 string,
  updateAlertFilters:     (filters: Partial<AlertActionFilter>) => void,
  filters:                AlertActionFilter,
  alarmList:              ListView<AlarmLabel>,
}

export const ActionBar: React.FC<ActionBarProps> = React.memo(({
  isAnySelected,
  selectedNumber,
  openConfirmationPopup,
  alertsToResolveNumber,
  showMessage,
  clearBulkAlerts,
  isBulkResolveEnabled,
  fetchUserLocations,
  userLocations,
  userId,
  currentTab,
  search,
  updateAlertFilters,
  filters,
  alarmList,
}) => {
  const { t } = useTranslation();

  const handleResolveBulkAlerts = () => {
    if (alertsToResolveNumber < 1) {
      showMessage({
        message: t('no-pending-alerts-selected-title'),
        description: t('no-pending-alerts-selected-description'),
        type: MessageType.INFO,
      });
    } else {
      openConfirmationPopup(
        t('resolve-alerts-bulk-title', { count: alertsToResolveNumber }),
        t('resolve-alerts-bulk-description', { count: alertsToResolveNumber }),
        t('resolve-alerts-action'),
        clearBulkAlerts
      )
    }
  };

  const onLocationGroupClear = () => {
    updateAlertFilters({ locationIds: [] });
    fetchUserLocations(userId as string);
  };

  return (
    <>
      <Row>
        <Col xs="12" lg="4">
          <FloGroupedDropdown
            id              = "locations"
            groupLabel      = {t('location-filter')}
            onClear         = {onLocationGroupClear}
            title           = {t('all-locations')}
            values          = {filters.locationIds}
            multiple
            reinitList      = {() => fetchUserLocations(userId as string)}
            isLoading       = {userLocations.isLoading}
            loadingMessage  = {() => t('Loading...')}
            full
            disableMemoryFilter
            options = {userLocations.rows?.map(loc => ({ value: loc.id || '', label: loc.nickname || loc.address || '--blank--', timezone: loc.timezone })) || []}
            onChange = {e => updateAlertFilters({ locationIds: e?.map((datum: any) => datum.value) || [] }) }
            page                  = {userLocations.page || 1}
            hasMore               = {(userLocations.total || 0) > (userLocations.rows?.length || 0)}
            onLoadOptions         = {(search: string | undefined, page: number) => fetchUserLocations(userId as string, [], page, search)}
          />
        </Col>
        <Col xs="12" lg="4">
          <FloFilterGroup label={t('alarm-filter')} onClear={() => updateAlertFilters({ alarmNames: [] })}>
            <FloDropdown
              id        = "alarms"
              title     = {t('all-alarms')}
              options   = {alarmList.rows?.map(a => ({ value: a.name, label: a.displayName })) || []}
              values    = {filters.alarmNames}
              onChange  = {e => updateAlertFilters({ alarmNames: e?.map((datum: any) => datum.value) || [] })}
              isLoading = {alarmList.isLoading}
              multiple
            />
          </FloFilterGroup>
        </Col>
        <Col xs="12" lg="4">
        <FloAlertFilterCheckbox
          checked     = {filters.activeOnly}
          groupLabel  = {t('active-alerts-only')}
          onChange    = {() => updateAlertFilters({activeOnly: !filters.activeOnly})} 
        />
        </Col>
      </Row>
      <Row>
        <Col xs="12" lg="4" className={styles.actionButtonWrapper}>
          <AlertExportButton
            className={classnames("float-right")}
            userId     = {userId}
            currentTab = {currentTab}
            search     = {search}
            filters    = {filters}
          />
          { isAnySelected &&
            <>
              <FloMenuButton
                className   = {classnames("float-right", styles.actionButton)}
                text        = {t('Actions')}
                name        = "actions-alerts-btn"
                menuItems   = {[
                  { id: 'resolve-alerts', onClick: handleResolveBulkAlerts, title: t('Resolve Selected Alerts'), disabled: !isBulkResolveEnabled },
                ]}
              />
              <span className = {classnames("float-right", styles.itemsSelected)} >{t('selected-items', { count: selectedNumber })}</span>
            </>
          }
        </Col>
      </Row>
      <Row>
        <Col xs="12">
          <div className={styles.divider} />
        </Col>
      </Row>
    </>
  )
})
